import { useAuth0 } from "@auth0/auth0-react";
import Loader from "../../components/Loader";
import styles from "./vehicles.module.css";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Slide, Snackbar, Typography } from "@mui/material";
import { ArrowLeft, Home, X } from "react-feather";
import React from "react";
import { Helmet } from "react-helmet";
import Footer from "../../components/Footer";
import { useMemo, useState } from "react";
import axios from "axios";
import { SEARCH_DEBOUNCE_MS, useDebouncedValue } from "./vehicleQuery";
import VehicleListView from "./components/listview/VehicleListView";
import VehicleGridView from "./components/gridview/VehicleGridView";
import VehicleAddForm from "./components/AddForm";
import { toast } from "react-smart-toaster";
import VehicleEditForm from "./components/EditForm";
import withCustomAuthenticationRequired from "../../auth/withCustomAuthenticationRequired";
import VehicleDetailPanel from "./components/VehicleDetailPanel";
import SendLinkDialog from "./components/SendLinkDialog";
import EditRequestDialog from "./components/EditRequestDialog";
import { useSendAccessLink } from "./components/guestPass";

const Vehicles = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const canUpdate = isAuthenticated && (user?.["https://propertydek.com/permissions"] || []).indexOf("vehicle:update") > -1;
  const canDelete = isAuthenticated && (user?.["https://propertydek.com/permissions"] || []).indexOf("vehicle:delete") > -1;
  const [isGridView, setIsGridView] = useState(false);
  const [hasGridRendered, setHasGridRendered] = useState(false);
  const [loading, setLoading] = useState(false);
  const [listChangeCounter, setListChangeCounter] = useState(0);
  const [filterData, setFilterData] = useState({ searchKey: "", property: "", apartment: "", type: "", status: "" });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [deleteVehicleModalOpen, setDeleteVehicleModalOpen] = useState(false);
  const [tenantModalOpen, setTenantModalOpen] = useState(false);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [selectedId, setSelectedId] = useState(0);
  const [editData, setEditData] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [sendLinkRow, setSendLinkRow] = useState(null);
  // Rejected new vehicle / guest: edit dialog and remove confirmation (row.id is the request id)
  const [editRequestRow, setEditRequestRow] = useState(null);
  const [deleteRequestRow, setDeleteRequestRow] = useState(null);
  const [deletingRequest, setDeletingRequest] = useState(false);

  // Single query for both views: the search box updates instantly, but the API only sees it after typing pauses
  const debouncedSearch = useDebouncedValue(filterData.searchKey, SEARCH_DEBOUNCE_MS);
  const query = useMemo(() => ({
    search: debouncedSearch.trim(),
    property: filterData.property,
    apartment: filterData.apartment,
    type: filterData.type,
    status: filterData.status,
  }), [debouncedSearch, filterData.property, filterData.apartment, filterData.type, filterData.status]);

  const handleView = (view) => {
    if (view === "grid") {
      setIsGridView(true);
      setHasGridRendered(true);
    } else {
      setIsGridView(false);
    }
  };

  // Lists are paginated on the server, so reload the active view (and the graph) after any change
  const onAdd = () => {
    setListChangeCounter((p) => p + 1);
  };

  const onUpdate = () => {
    setListChangeCounter((p) => p + 1);
  };

  // After "Send payment link": reload the active view so the pass status updates
  const reloadList = () => {
    setListChangeCounter((p) => p + 1);
  };

  // Success messages on this page use the bottom-right snackbar
  const notifySuccess = (message) => {
    setSnackbarMsg(message);
    setSnackbarOpen(true);
  };

  const [, sendAccessLink] = useSendAccessLink(notifySuccess);

  // Row actions shared by the list, grid and mobile views
  const actions = {
    // Active guest pass: text the guest a fresh gate access link
    sendAccessLink: (row) => sendAccessLink(row?.vehicle_id || row?.id),
    canUpdate,
    notifySuccess,
    onChanged: reloadList,
    openDetails: (row) => setDetailRow(row),
    openEdit: (row) => {
      setEditData(row);
      setEditModalOpen(true);
    },
    openDelete: (row) => {
      setSelectedId(row?.vehicle_id || row?.id);
      setDeleteVehicleModalOpen(true);
    },
    openSendLink: (row) => setSendLinkRow(row),
    canDelete,
    openEditRequest: (row) => setEditRequestRow(row),
    openDeleteRequest: (row) => setDeleteRequestRow(row),
  };

  const deleteRequestConfirm = async () => {
    if (!deleteRequestRow) return;
    setDeletingRequest(true);
    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });
      const response = await axios.delete(process.env.REACT_APP_TENANT_API_URL + "v1/vehicle-requests/" + deleteRequestRow.id, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteRequestRow(null);
      notifySuccess(response?.data?.message || "Rejected vehicle removed.");
      reloadList();
    } catch (error) {
      console.error("[Vehicles] Failed to remove rejected vehicle", error);
      toast.error(error?.response?.data?.message || "Unable to remove the vehicle. Please try again.");
    } finally {
      setDeletingRequest(false);
    }
  };

  const deleteConfirm = async () => {
    const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

    setDeleteVehicleModalOpen(false);
    setLoading(true);

    axios.delete(process.env.REACT_APP_TENANT_API_URL + "v1/vehicles/" + selectedId, {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    }).then((response) => {
      setLoading(false);

      setSnackbarMsg(response.data.message);
      setSnackbarOpen(true);

      setListChangeCounter((p) => p + 1);
      setSelectedId(0);
    }).catch((error) => {
      if (typeof error.response !== "undefined")
        toast.error(error.response.data.message);
      setLoading(false);
    });
  }

  return (<>
    {loading && <Loader />}

    <Helmet title="Vehicle" />

    <div className={`${styles.pageTitleWrap}`}>
      <Typography display="inline" className={`${styles.pageTitle}`}>Vehicles</Typography>
      <nav aria-label="breadcrumb">
        <ol className={`${styles.breadcrumb}`}>
          <li className={`${styles.breadcrumbItem}`}><span><Home /></span>Vehicle</li>
          <li className={`${styles.breadcrumbItem} ${styles.active}`}>Lists</li>
        </ol>
      </nav>
    </div>

    {/* List View */}
    <div style={{ display: isGridView ? "none" : "block" }}>
      <VehicleListView
        query={query}
        refreshKey={listChangeCounter}
        onListChange={reloadList}
        actions={actions}
        isGridView={isGridView}
        handleView={handleView.bind(this)}
        filterData={filterData}
        setFilterData={setFilterData.bind(this)}
        setAddModalOpen={setAddModalOpen.bind(this)}
        setTenantModalOpen={setTenantModalOpen.bind(this)}
        setTenantDetails={setTenantDetails.bind(this)}
      />
    </div>

    {/* Grid View */}
    {hasGridRendered && (<div style={{ display: isGridView ? "block" : "none" }}>
      <VehicleGridView
        query={query}
        refreshKey={listChangeCounter}
        actions={actions}
        isGridView={isGridView}
        handleView={handleView.bind(this)}
        filterData={filterData}
        setFilterData={setFilterData.bind(this)}
        setAddModalOpen={setAddModalOpen.bind(this)}
        setTenantModalOpen={setTenantModalOpen.bind(this)}
        setTenantDetails={setTenantDetails.bind(this)}
      />
    </div>)}

    <Dialog
      open={addModalOpen}
      onClose={(e) => setAddModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setAddModalOpen(false)}>
          <ArrowLeft />
        </button>
        Add New Vehicle
        <button onClick={(e) => setAddModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <VehicleAddForm
          setAddModalOpen={setAddModalOpen.bind(this)}
          setLoading={setLoading.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMsg={setSnackbarMsg.bind(this)}
          onSubmitLocal={onAdd.bind(this)}
        />
      </DialogContent>
    </Dialog>

    <Dialog
      open={editModalOpen}
      onClose={(e) => setEditModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setEditModalOpen(false)}>
          <ArrowLeft />
        </button>
        Update Vehicle
        <button onClick={(e) => setEditModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <VehicleEditForm
          setAddModalOpen={setEditModalOpen.bind(this)}
          setLoading={setLoading.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMsg={setSnackbarMsg.bind(this)}
          onSubmitLocal={onUpdate.bind(this)}
          editData={editData}
        />
      </DialogContent>
    </Dialog>

    <Dialog
      open={deleteVehicleModalOpen}
      onClose={(e) => setDeleteVehicleModalOpen(false)}
      fullWidth
      maxWidth="sm"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp"
    >
      <DialogTitle id="alert-dialog-title">
        Would you like to delete this vehicle?
        <button onClick={(e) => setDeleteVehicleModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">The vehicle information will be removed immediately. You can't undo this action.</label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={(e) => deleteConfirm(false)} className="CancelPopupBU">Delete</Button>
        <Button onClick={(e) => setDeleteVehicleModalOpen(false)} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={tenantModalOpen}
      onClose={(e) => setTenantModalOpen(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      className="NewPopUp MobileForm"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="alert-dialog-title">
        <button className={`${styles.BackAddBU}`} onClick={(e) => setTenantModalOpen(false)}>
          <ArrowLeft />
        </button>
        Tenant Details
        <button onClick={(e) => setTenantModalOpen(false)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        <div className={`${styles.TenantDEPopupSec}`}>
          <div className={`${styles.TenantDEImage}`}>
            <img src={tenantDetails?.tenant_image} />
          </div>
          <p className={`${styles.Name}`}>{tenantDetails?.full_name}</p>
          <div className={`${styles.Info}`}><span>Email:</span> {tenantDetails?.email}</div>
          <div className={`${styles.Info}`}><span>Phone:</span> {tenantDetails?.phone}</div>
          <div className={`${styles.Info}`}><span>Propety:</span> {tenantDetails?.property}</div>
          <div className={`${styles.Info}`}><span>Unit:</span> {tenantDetails?.unit}</div>
        </div>
      </DialogContent>
    </Dialog>

    <Dialog
      open={Boolean(detailRow)}
      onClose={() => setDetailRow(null)}
      aria-labelledby="vehicle-detail-title"
      className="NewPopUp MobileForm"
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="vehicle-detail-title">
        <button type="button" className={`${styles.BackAddBU}`} onClick={() => setDetailRow(null)}>
          <ArrowLeft />
        </button>
        {detailRow?.license ? `Vehicle ${detailRow.license}` : "Vehicle"}
        <button type="button" onClick={() => setDetailRow(null)}>
          <X />
        </button>
      </DialogTitle>
      <DialogContent className={`${styles.RequestBodyPop}`}>
        {detailRow && (<VehicleDetailPanel
          row={detailRow}
          canUpdate={canUpdate}
          onChanged={() => { setDetailRow(null); reloadList(); }}
          onEdit={(row) => { setDetailRow(null); actions.openEdit(row); }}
          onDelete={(row) => { setDetailRow(null); actions.openDelete(row); }}
          onEditRequest={(row) => { setDetailRow(null); actions.openEditRequest(row); }}
          onDeleteRequest={canDelete ? (row) => { setDetailRow(null); actions.openDeleteRequest(row); } : undefined}
          notifySuccess={notifySuccess}
        />)}
      </DialogContent>
    </Dialog>

    {sendLinkRow && (<SendLinkDialog
      initialPropertyId={sendLinkRow?.property_id}
      initialTenantInfoId={sendLinkRow?.tenant_info_id}
      onClose={() => setSendLinkRow(null)}
      onSent={(message) => { setSendLinkRow(null); notifySuccess(message); }}
    />)}

    {editRequestRow && (<EditRequestDialog
      requestId={editRequestRow.id}
      onClose={() => setEditRequestRow(null)}
      onSaved={() => { setEditRequestRow(null); reloadList(); }}
      notifySuccess={notifySuccess}
    />)}

    <Dialog open={Boolean(deleteRequestRow)} onClose={deletingRequest ? undefined : () => setDeleteRequestRow(null)} fullWidth maxWidth="sm" className="NewPopUp">
      <DialogTitle>
        Remove this rejected vehicle?
        <button type="button" onClick={() => setDeleteRequestRow(null)} disabled={deletingRequest}><X /></button>
      </DialogTitle>
      <DialogContent>
        <div className="ModalFormGroup">
          <label className="PopupBodyText">
            {deleteRequestRow?.license ? `${deleteRequestRow.license} will be removed from the vehicle list. ` : "It will be removed from the vehicle list. "}
            The {deleteRequestRow?.is_guest === 1 ? "tenant and guest" : "tenant"} can't resubmit it.
          </label>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={deleteRequestConfirm} disabled={deletingRequest} className="CancelPopupBU">{deletingRequest ? "Removing..." : "Remove"}</Button>
        <Button onClick={() => setDeleteRequestRow(null)} disabled={deletingRequest} className="SubmitPopupBU">Cancel</Button>
      </DialogActions>
    </Dialog>

    <Snackbar
      open={snackbarOpen}
      onClose={(e) => setSnackbarOpen(false)}
      TransitionComponent={Slide}
      message={snackbarMsg}
      autoHideDuration={10000}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      action={
        <IconButton
          size="small"
          aria-label="close"
          color="inherit"
          onClick={(e) => setSnackbarOpen(false)}
        >
          <X />
        </IconButton>
      }
    />

    <Footer />
  </>);
};

export default withCustomAuthenticationRequired(Vehicles, {
  onRedirecting: () => <Loader />,
});